import { useTheme } from "@mui/material";
import type { LucideIcon } from "lucide-react";
import { type FC, useState } from "react";
import type { ObjPathProxy } from "ts-object-path";
import { AdvisorConfirmDrawer } from "web_myplann/Advisor/Components/AdvisorConfirmDrawer";
import type { AdvisorConfirmCopy } from "web_myplann/Advisor/Types/AdvisorConfirmCopy";
import type { AdvisorConfirmTarget } from "web_myplann/Advisor/Types/AdvisorConfirmTarget";
import type { AgentAssignedListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";

type Props = {
	advisor?: AgentAssignedListItemResponseDTO;
	visible: boolean;
	icon: LucideIcon;
	ariaLabel: ObjPathProxy<MyPlannResourceDictionary, string>;
	copy: AdvisorConfirmCopy;
	isPending: boolean;
	onConfirm: (
		advisor: AgentAssignedListItemResponseDTO,
		onSuccess: () => void,
	) => void;
};

export const AdvisorConfirmAction: FC<Props> = ({
	advisor,
	visible,
	icon: Icon,
	ariaLabel,
	copy,
	isPending,
	onConfirm,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [target, setTarget] = useState<AdvisorConfirmTarget>();

	const openConfirm = () => {
		if (!advisor) return;
		setTarget({ advisor, copy });
		setIsConfirmOpen(true);
	};

	const closeConfirm = () => setIsConfirmOpen(false);

	const confirm = () => {
		if (!target || !isConfirmOpen) return;
		onConfirm(target.advisor, closeConfirm);
	};

	return (
		<>
			{visible && (
				<HeaderIconButton
					aria-label={t(ariaLabel, { name: advisor?.name ?? "" })}
					disabled={isPending}
					onClick={openConfirm}
				>
					<Icon size={theme.iconSize.md} color={theme.palette.grey[700]} />
				</HeaderIconButton>
			)}
			<AdvisorConfirmDrawer
				open={isConfirmOpen}
				copy={target?.copy ?? copy}
				advisorName={target?.advisor.name ?? ""}
				loading={isPending}
				onClose={closeConfirm}
				onConfirm={confirm}
			/>
		</>
	);
};
